/**
 * Pixa Asistan — uyarı motorunun bulgularını gösteren ve aksiyon alınan ekran.
 *
 * Sekmeler: Sor, Bulgular (grup kartları + aksiyonlar), Müşteri Soruları (Pazaryeri Asistanı:
 * kanal göstergeleri + gelen kutusu + AI taslağıyla pazaryerine cevap), Kurallar.
 *
 * Bulgular GRUP olarak geliyor (3381 satırlık bir sorun tek kartta "Trendyol: 505
 * kayıt" şeklinde). Detay istenirse ilgili sayfaya filtreli gidiliyor; binlerce satır
 * burada listelenmiyor.
 */
import React, { useState, useEffect } from 'react';
import {
  API, T, MONO, FONT, Badge, Button, Card, ConfirmDangerModal, Eyebrow, Grid, Icon, Loader,
  Tabs, Thumb, Toast, axios, fmtDateTime, fmtN, inputStyle, money, timeAgo, tint, useData,
  useIntegrations, useLang, useList, upperUi,
} from './shared';
import { addTranslations } from '../i18n';
import { useViewport } from '../poyais-ui/hooks';
import { takeFocus } from './focus';
import { Findings } from './Findings';
import { MarketMark, marketColor } from './marketplaces';
import { titleCase } from './reportLabels';

// Susturma süresiz: kullanıcı kuralı kendisi tekrar açana kadar uyarı üretmez.
// (Backend mute_until'ı 'şimdi + saat' olarak hesapladığı için pratikte sonsuz bir tarih veriliyor.)
const MUTE_FOREVER_HOURS = 24 * 365 * 100;

export function AsistanPage({ clientId }: { clientId: number }) {
  const [tab, setTab] = useState('ask');
  // Bir bulgudan gelindiyse doğrudan ilgili sekme açılır.
  useEffect(() => {
    const f = takeFocus('pixa-asistan');
    if (f?.tab) setTab(f.tab);
  }, []);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontFamily: FONT }}>
      <Tabs value={tab} onChange={setTab} tabs={[
        { id: 'ask', label: 'Sor' },
        { id: 'findings', label: 'Bulgular' },
        { id: 'questions', label: 'Müşteri Soruları' },
        { id: 'rules', label: 'Kurallar' },
      ]} />
      {tab === 'ask' ? <Ask clientId={clientId} />
        : tab === 'findings' ? <Findings clientId={clientId} onGoQuestions={() => setTab('questions')} />
        : tab === 'questions' ? <Questions clientId={clientId} />
        : <Rules clientId={clientId} />}
    </div>
  );
}

// ── Sor (doğal dilde soru-cevap) ─────────────────────────────────────────────

const SUGGESTED = [
  'Bu ay işler nasıl gidiyor?',
  'En acil ilgilenmem gereken şey ne?',
  'Hangi ürünlerin stoğu bitmek üzere?',
  'Hangi pazaryeri en çok ciro getiriyor?',
  'İade oranım yüksek mi?',
  'Kârlılığımı ne düşürüyor?',
];

function Ask({ clientId }: { clientId: number }) {
  const { t } = useLang();
  const [turns, setTurns] = useState<{ role: 'user' | 'assistant'; content: string }[]>([]);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const endRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [turns, busy]);
  useEffect(() => { setTurns([]); setErr(null); }, [clientId]);

  const send = async (text?: string) => {
    const question = (text ?? q).trim();
    if (!question || busy) return;
    const history = turns.slice(-6);
    setTurns(ts => [...ts, { role: 'user', content: question }]);
    setQ(''); setBusy(true); setErr(null);
    try {
      const r = await axios.post(`${API}/pixa/${clientId}/ask`, { question, history });
      setTurns(ts => [...ts, { role: 'assistant', content: r.data?.answer || '—' }]);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || t('Yanıt alınamadı.'));
    } finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Card pad={0}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: T.text }}>{t('Pixa Verinize Sorun')}</div>
          <div style={{ fontSize: 11.5, color: T.textDim, marginTop: 3, lineHeight: 1.55 }}>
            {t('Siparişler, stok, kârlılık ve açık bulgular üzerinden yanıtlar. Yalnızca okur — işlem yapmaz.')}
          </div>
        </div>

        <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12,
          maxHeight: 460, overflowY: 'auto' as const }}>
          {turns.length === 0 && !busy && (
            <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 7 }}>
              {SUGGESTED.map(s => (
                <Button key={s} variant="ghost" color={T.accent} onClick={() => send(s)}
                  style={{ padding: '6px 12px', fontSize: 11.5 }}>{s}</Button>
              ))}
            </div>
          )}
          {turns.map((m, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: '86%', padding: '10px 13px', borderRadius: 12, fontSize: 12.5, lineHeight: 1.7,
                whiteSpace: 'pre-wrap' as const,
                background: m.role === 'user' ? T.accent : T.card,
                color: m.role === 'user' ? '#12100A' : T.text,
                fontWeight: m.role === 'user' ? 600 : 400,
              }}>{m.content}</div>
            </div>
          ))}
          {busy && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: T.textDim }}>
              <div style={{ width: 14, height: 14, border: `2px solid ${T.accent}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              {t('Yanıt hazırlanıyor…')}
              <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            </div>
          )}
          {err && <div style={{ fontSize: 11.5, color: T.redText, lineHeight: 1.6 }}>{err}</div>}
          <div ref={endRef} />
        </div>

        <div style={{ padding: '12px 18px', borderTop: `1px solid ${T.border}`, display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <textarea value={q} onChange={e => setQ(e.target.value)} rows={1}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={t('Sorunuzu yazın…')}
            style={{ ...inputStyle, flex: 1, resize: 'none' as const, minHeight: 38, maxHeight: 120, lineHeight: 1.6 }} />
          <Button variant="solid" color={T.accent} disabled={busy || !q.trim()} onClick={() => send()}
            style={{ padding: '9px 18px', fontSize: 12 }}>{t('Sor')}</Button>
          {turns.length > 0 && (
            <Button variant="ghost" color={T.textDim} onClick={() => { setTurns([]); setErr(null); }}
              style={{ padding: '9px 12px', fontSize: 11.5 }}>{t('Temizle')}</Button>
          )}
        </div>
      </Card>
    </div>
  );
}

// ── Müşteri Soruları — Pazaryeri Asistanı ────────────────────────────────────
//
// Pixa'nın /questions verisi hakkında bilinmesi gerekenler (canlı veriyle doğrulandı):
// - Pazaryeri yalnızca resourceId ile geliyor; adı /questions/stats.byPlatform'dan
//   (yoksa /integrations'tan) eşleniyor.
// - "AutoClosed" = satıcı süresinde cevaplamadığı için pazaryeri soruyu kapattı
//   (son mesaj System'den: "Sorunuz satıcı tarafından yanıtlanmamıştır…"). Pixa'nın
//   onlyPending filtresi bunları da "bekleyen" sayıyor; bu yüzden liste tek seferde
//   çekilip durum istemcide ayrılıyor.
// - creationDate Pixa'nın içe aktarma tarihi (yüzlercesi aynı gün); gerçek soru anı
//   ilk Customer mesajının platformCreationDate'i.
// - Ürün bilgisi yalnızca bazı pazaryerlerinde (şu an Trendyol) dolu geliyor.

addTranslations({
  'Pazaryeri Asistanı': 'Marketplace Assistant',
  'Pazaryeri soruları satışı belirler.': 'Marketplace questions decide marketplace sales.',
  'Trendyol, Hepsiburada ve diğer kanallardaki müşteri sorularını tek gelen kutusunda toplayın. AI, kataloğunuza dayanarak cevap taslağını saniyeler içinde hazırlar; siz onaylayıp yayınlarsınız.':
    'Bring customer questions from Trendyol, Hepsiburada and other channels into one inbox. AI drafts an answer from your catalog in seconds; you approve and publish.',
  'Toplam Soru': 'Total Questions', 'Cevaplanma Oranı': 'Answer Rate', 'Ort. İlk Cevap Süresi': 'Median First Reply',
  'Kaçırılan Soru': 'Missed Questions', 'pazaryeri tarafından kapatıldı': 'closed by the marketplace',
  'cevaplanan / toplam': 'answered / total', 'ortanca, cevaplanan sorularda': 'median, answered questions',
  'Kanal Performansı': 'Channel Performance', 'Cevaplanan': 'Answered', 'Kaçırılan': 'Missed', 'Reddedilen': 'Rejected',
  'Bekleyen': 'Pending', 'Bekliyor': 'Pending', 'Cevaplandı': 'Answered', 'Kaçırıldı': 'Missed', 'Reddedildi': 'Rejected',
  'Onay Bekliyor': 'Awaiting Approval', 'Bildirildi': 'Reported', 'cevap oranı': 'answer rate', 'ort. cevap': 'median reply',
  'soru süresinde cevaplanmadığı için pazaryeri tarafından kapatıldı.': 'questions were closed by the marketplace because they were not answered in time.',
  'Bu sorular, müşterinin satın alma kararı verdiği anda cevapsız kaldı.': 'These questions went unanswered right when the shopper was deciding to buy.',
  'Kaçırılanları Gör': 'View missed', 'Soru, ürün veya cevap ara…': 'Search questions, products or answers…',
  'Tüm Kanallar': 'All Channels', 'Bu filtrede soru yok.': 'No questions in this filter.',
  'Bir soru seçin': 'Select a question', 'Soruyu seçtiğinizde konuşma, ürün ve cevap alanı burada açılır.': 'The conversation, product and reply box open here when you pick a question.',
  'Müşteri': 'Customer', 'Mağaza': 'Store', 'Sistem': 'System', 'Soru No': 'Question No', 'Sipariş': 'Order',
  'Stok': 'Stock', 'SKU': 'SKU', 'sonra cevaplandı': 'to answer', 'Cevap süresi': 'Reply time',
  'Bu soru süresinde cevaplanmadığı için pazaryeri tarafından kapatıldı; artık cevap gönderilemez.':
    'This question was closed by the marketplace because it was not answered in time; it can no longer be answered.',
  'Pazaryeri bu soruyu reddetti; cevap gönderilemez.': 'The marketplace rejected this question; it cannot be answered.',
  'Bu sorunun cevaplama süresi doldu.': 'The answer window for this question has expired.',
  'Bu soru cevaplandı.': 'This question has been answered.',
  'AI ile Cevap Yaz': 'Write with AI', 'AI taslağı hazırlanıyor…': 'AI is drafting…', 'Yeniden Yaz': 'Rewrite',
  'Hazır cevaplar': 'Saved replies', 'Cevabınız': 'Your answer', 'Pazaryerinde yayınlanacak cevabı yazın…': 'Write the answer to publish on the marketplace…',
  'Cevap pazaryerinde herkese açık yayınlanır ve geri alınamaz.': 'The answer is published publicly on the marketplace and cannot be undone.',
  'Pazaryerine Gönder': 'Send to Marketplace', 'Gönderildi': 'Sent', 'Gönder': 'Send',
  'Cevabı pazaryerinde yayınla': 'Publish the answer on the marketplace',
  'Bu cevap pazaryerinde herkese açık olarak yayınlanacak ve geri alınamaz.': 'This answer will be published publicly on the marketplace and cannot be undone.',
  'Son cevaplama': 'Answer by', 'Geri': 'Back', 'karakter': 'characters', 'Ürün bilgisi yok': 'No product info',
  'Daha Fazla': 'Load more', 'soru': 'questions',
});

type QStatus = 'pending' | 'approval' | 'answered' | 'missed' | 'rejected' | 'reported' | 'other';
function qStatus(raw: string): QStatus {
  const s = (raw || '').toLowerCase().replace(/[\s_-]/g, '');
  if (s === 'answered') return 'answered';
  if (s === 'autoclosed' || s === 'closed' || s === 'expired') return 'missed';
  if (s === 'rejected') return 'rejected';
  if (s === 'reported') return 'reported';
  if (s.includes('approve') || s.includes('approval')) return 'approval';
  if (s.startsWith('waiting') || ['pending', 'open', 'new', 'unanswered', 'created'].includes(s)) return 'pending';
  return 'other';
}
const STATUS_META: Record<QStatus, { label: string; color: string; text: string; hint?: string }> = {
  pending:  { label: 'Bekliyor',      color: T.accent, text: T.amberDeepText },
  approval: { label: 'Onay Bekliyor', color: T.blue,   text: T.blueText },
  answered: { label: 'Cevaplandı',    color: T.green,  text: T.greenText },
  missed:   { label: 'Kaçırıldı',     color: T.red,    text: T.redText, hint: 'pazaryeri tarafından kapatıldı' },
  rejected: { label: 'Reddedildi',    color: T.amber,  text: T.amberText },
  reported: { label: 'Bildirildi',    color: T.textDim, text: T.textDim },
  other:    { label: '',              color: T.textDim, text: T.textDim },
};

const tsOf = (v: any) => { const d = new Date(v); return isNaN(d.getTime()) ? 0 : d.getTime(); };

function fmtDur(ms: number | null, t: (s: string) => string) {
  if (ms === null || ms < 0) return '—';
  const m = Math.round(ms / 60000);
  const en = t('Müşteri') === 'Customer';
  if (m < 1) return en ? '< 1 min' : '< 1 dk';
  if (m < 60) return en ? `${m} min` : `${m} dk`;
  const h = Math.floor(m / 60), mm = m % 60;
  if (h < 24) return en ? `${h} h${mm ? ` ${mm} min` : ''}` : `${h} sa${mm ? ` ${mm} dk` : ''}`;
  const d = Math.floor(h / 24), hh = h % 24;
  return en ? `${d} d${hh ? ` ${hh} h` : ''}` : `${d} gün${hh ? ` ${hh} sa` : ''}`;
}

const median = (xs: number[]) => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b); const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

/** Ham soruyu ekranın ihtiyaç duyduğu şekle getirir. */
function enrich(q: any, marketName: (rid: any) => string) {
  const convs: any[] = [...(q.conversations || [])].sort((a, b) => tsOf(a.platformCreationDate) - tsOf(b.platformCreationDate));
  const firstCustomer = convs.find(c => c.from === 'Customer');
  const firstMerchant = convs.find(c => c.from === 'Merchant' && (!firstCustomer || tsOf(c.platformCreationDate) >= tsOf(firstCustomer.platformCreationDate)));
  const askedAt = firstCustomer?.platformCreationDate || q.creationDate;
  const replyMs = firstCustomer && firstMerchant ? tsOf(firstMerchant.platformCreationDate) - tsOf(firstCustomer.platformCreationDate) : null;
  const img = q.product?.images?.[0];
  const status = qStatus(q.status);
  const expired = !!Number(q.isExpired);
  return {
    ...q, convs, askedAt, replyMs, status, expired,
    market: marketName(q.resourceId),
    text: firstCustomer?.content || q.description || '',
    productName: q.product?.name || (q.subject && q.subject.length > 30 ? q.subject : ''),
    topic: q.subject && q.subject.length <= 30 ? q.subject : '',
    thumb: img?.link || img?.path || null,
    answerable: (status === 'pending') && !expired,
  };
}

function MarketChip({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' }) {
  const s = size === 'sm' ? 16 : 20;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: size === 'sm' ? 11 : 12, fontWeight: 700, color: T.text, whiteSpace: 'nowrap' as const }}>
      <MarketMark name={name} size={s} />
      {name || '—'}
    </span>
  );
}

function StatusPill({ status, raw }: { status: QStatus; raw?: string }) {
  const { t } = useLang();
  const m = STATUS_META[status];
  return (
    <span title={m.hint ? t(m.hint) : undefined} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10.5, fontWeight: 800,
      color: m.text, background: tint(m.color, 12), border: `1px solid ${tint(m.color, 40)}`, borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap' as const }}>
      <span style={{ width: 6, height: 6, borderRadius: 999, background: m.color }} />
      {m.label ? t(m.label) : (raw || '—')}
    </span>
  );
}

function Kpi({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '14px 16px', minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: T.textDim, marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: color || T.text, letterSpacing: '-0.02em', lineHeight: 1.1 }}>{value}</div>
    </div>
  );
}

function Questions({ clientId }: { clientId: number }) {
  const { t } = useLang();
  const { isMobile } = useViewport();
  const { rows, loading, error, reload } = useList(clientId, '/questions', { limit: 200, sort: 'newest' });
  const { data: stats } = useData(clientId, '/questions/stats');
  const integrations = useIntegrations(clientId);
  const [filter, setFilter] = useState<'pending' | 'answered' | 'missed' | 'rejected' | 'all' | null>(null);
  const [market, setMarket] = useState('');
  const [search, setSearch] = useState('');
  const [selId, setSelId] = useState<number | null>(null);
  const [shown, setShown] = useState(40);
  const [sentIds, setSentIds] = useState<Set<number>>(new Set());

  const marketName = React.useCallback((rid: any) => {
    const s = (stats?.byPlatform || []).find((p: any) => String(p.resourceId) === String(rid));
    if (s?.platformName) return s.platformName;
    const i = integrations.find((x: any) => String(x.resourceId) === String(rid));
    return i?.resourceName || `#${rid}`;
  }, [stats, integrations]);

  const all = React.useMemo(() => rows.map((q: any) => enrich(q, marketName))
    .map((q: any) => sentIds.has(q.id) ? { ...q, status: 'approval' as QStatus, answerable: false } : q)
    .sort((a: any, b: any) => tsOf(b.askedAt) - tsOf(a.askedAt)), [rows, marketName, sentIds]);

  const byMarket = market ? all.filter((q: any) => q.market === market) : all;
  const counts = {
    pending: byMarket.filter((q: any) => q.status === 'pending' || q.status === 'approval').length,
    answered: byMarket.filter((q: any) => q.status === 'answered').length,
    missed: byMarket.filter((q: any) => q.status === 'missed').length,
    rejected: byMarket.filter((q: any) => q.status === 'rejected').length,
    all: byMarket.length,
  };
  // Varsayılan sekme: bekleyen varsa onlar, yoksa tümü (boş "Bekleyen" ekranı açılmasın).
  const activeFilter = filter ?? (counts.pending > 0 ? 'pending' : 'all');
  const needle = search.trim().toLocaleLowerCase('tr');
  const list = byMarket.filter((q: any) => {
    if (activeFilter === 'pending' && !(q.status === 'pending' || q.status === 'approval')) return false;
    if (activeFilter !== 'pending' && activeFilter !== 'all' && q.status !== activeFilter) return false;
    if (!needle) return true;
    return [q.text, q.productName, q.topic, q.product?.sku, ...q.convs.map((c: any) => c.content)]
      .some((s: any) => String(s || '').toLocaleLowerCase('tr').includes(needle));
  });
  useEffect(() => { setShown(40); }, [activeFilter, market, needle]);
  const selected = all.find((q: any) => q.id === selId) || null;
  // Masaüstünde liste değişince ilk soru otomatik seçilir; mobilde liste önce görünür.
  useEffect(() => {
    if (isMobile) return;
    if (!list.length) { if (selId !== null) setSelId(null); return; }
    if (!list.some((q: any) => q.id === selId)) setSelId(list[0].id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, list.map((q: any) => q.id).join(',')]);

  // Göstergeler (tüm kanallar ya da seçili kanal)
  const replyTimes = byMarket.filter((q: any) => q.replyMs !== null && q.replyMs >= 0).map((q: any) => q.replyMs as number);
  const decided = counts.answered + counts.missed + counts.rejected;
  const answerRate = decided ? Math.round((counts.answered / decided) * 100) : null;
  const markets: string[] = Array.from(new Set(all.map((q: any) => q.market)));
  const perMarket = markets.map(m => {
    const qs = all.filter((q: any) => q.market === m);
    const ans = qs.filter((q: any) => q.status === 'answered').length;
    const mis = qs.filter((q: any) => q.status === 'missed').length;
    const rej = qs.filter((q: any) => q.status === 'rejected').length;
    const pen = qs.filter((q: any) => q.status === 'pending' || q.status === 'approval').length;
    const med = median(qs.filter((q: any) => q.replyMs !== null && q.replyMs >= 0).map((q: any) => q.replyMs));
    return { m, total: qs.length, ans, mis, rej, pen, med, rate: ans + mis + rej ? Math.round(ans / (ans + mis + rej) * 100) : null };
  }).sort((a, b) => b.total - a.total);

  if (error) return <Card pad={20}><div style={{ fontSize: 12, color: T.redText }}>{error}</div></Card>;
  if (loading && !rows.length) return <Loader />;

  const listPane = (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, borderRight: isMobile ? 'none' : `1px solid ${T.border}` }}>
      <div style={{ padding: 12, borderBottom: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' as const }}>
          {([['pending', 'Bekleyen'], ['answered', 'Cevaplanan'], ['missed', 'Kaçırılan'], ['rejected', 'Reddedilen'], ['all', 'Tümü']] as const).map(([id, label]) => {
            const on = activeFilter === id;
            return (
              <button key={id} onClick={() => setFilter(id)} style={{ flexShrink: 0, padding: '5px 9px', borderRadius: 8, fontFamily: 'inherit', fontSize: 11.5,
                fontWeight: on ? 800 : 600, cursor: 'pointer', border: `1px solid ${on ? tint(T.accent, 45) : 'transparent'}`,
                background: on ? tint(T.accent, 12) : 'transparent', color: on ? T.text : T.textMid, display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                {t(label)}
                <span style={{ fontSize: 10, fontWeight: 800, color: id === 'missed' && counts.missed ? T.redText : T.textDim,
                  background: 'var(--py-subtle-bg)', borderRadius: 999, padding: '1px 6px' }}>{counts[id]}</span>
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('Soru, ürün veya cevap ara…')}
            style={{ ...inputStyle, flex: 1, minWidth: 0, fontSize: 12.5, padding: '8px 10px' }} />
          {markets.length > 1 && (
            <select value={market} onChange={e => setMarket(e.target.value)} style={{ ...inputStyle, maxWidth: 140, fontSize: 12 }}>
              <option value="">{t('Tüm Kanallar')}</option>
              {markets.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          )}
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto' as const, minHeight: 0 }}>
        {list.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center' as const, fontSize: 12.5, color: T.textDim }}>{t('Bu filtrede soru yok.')}</div>
        ) : list.slice(0, shown).map((q: any) => {
          const on = q.id === selId && !isMobile;
          return (
            <button key={q.id} onClick={() => setSelId(q.id)} style={{ width: '100%', textAlign: 'left' as const, display: 'flex', gap: 10,
              padding: '12px 14px', border: 'none', borderBottom: `1px solid ${T.border}`, cursor: 'pointer', fontFamily: 'inherit',
              background: on ? tint(T.accent, 9) : 'transparent', boxShadow: on ? `inset 3px 0 0 ${T.accent}` : 'none' }}>
              {q.thumb ? <Thumb url={q.thumb} size={40} />
                : <MarketMark name={q.market} size={40} />}
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'space-between', marginBottom: 3 }}>
                  <MarketChip name={q.market} size="sm" />
                  <span style={{ fontSize: 10.5, color: T.textFaint, flexShrink: 0 }}>{timeAgo(q.askedAt)}</span>
                </span>
                <span style={{ fontSize: 12.5, fontWeight: q.status === 'pending' ? 800 : 600, color: T.text, lineHeight: 1.4,
                  overflow: 'hidden', display: '-webkit-box' as any, WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any }}>{q.text || '—'}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5, minWidth: 0 }}>
                  <StatusPill status={q.status} raw={q.status === 'other' ? q.status : undefined} />
                  <span style={{ fontSize: 10.5, color: T.textDim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const, minWidth: 0 }}>
                    {q.productName || q.topic}
                  </span>
                </span>
              </span>
            </button>
          );
        })}
        {shown < list.length && (
          <div style={{ padding: 12, textAlign: 'center' as const }}>
            <Button variant="ghost" color={T.accent} onClick={() => setShown(n => n + 40)} style={{ padding: '7px 14px', fontSize: 11.5 }}>
              {t('Daha Fazla')} ({list.length - shown})
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  const threadPane = selected
    ? <Thread key={selected.id} clientId={clientId} q={selected} onBack={isMobile ? () => setSelId(null) : undefined}
        onSent={() => { setSentIds(s => new Set(s).add(selected.id)); reload(); }} />
    : (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 40, textAlign: 'center' as const, height: '100%' }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: T.text }}>{t('Bir soru seçin')}</div>
        <div style={{ fontSize: 12.5, color: T.textDim, maxWidth: 280 }}>{t('Soruyu seçtiğinizde konuşma, ürün ve cevap alanı burada açılır.')}</div>
      </div>
    );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Ürün başlığı + göstergeler */}
      <div style={{ background: `linear-gradient(135deg, ${tint(T.accent, 7)}, var(--py-surface) 55%)`, border: `1px solid ${T.border}`, borderRadius: 18,
        padding: isMobile ? 16 : '22px 24px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(260px, 1fr) minmax(0, 1.55fr)', gap: isMobile ? 16 : 24, alignItems: 'center' }}>
        <div>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10.5, fontWeight: 800, letterSpacing: '0.1em', color: T.amberDeepText,
            background: tint(T.accent, 16), borderRadius: 999, padding: '4px 10px', marginBottom: 12 }}>
            <Icon.sparkle size={12} sw={2} /> {upperUi(t('Pazaryeri Asistanı'))}
          </span>
          <div style={{ fontSize: isMobile ? 19 : 22, fontWeight: 800, color: T.text, letterSpacing: '-0.02em', lineHeight: 1.2, marginBottom: 8 }}>
            {t('Pazaryeri soruları satışı belirler.')}
          </div>
          <div style={{ fontSize: 12.5, color: T.textMid, lineHeight: 1.6 }}>
            {t('Trendyol, Hepsiburada ve diğer kanallardaki müşteri sorularını tek gelen kutusunda toplayın. AI, kataloğunuza dayanarak cevap taslağını saniyeler içinde hazırlar; siz onaylayıp yayınlarsınız.')}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))', gap: 10 }}>
          <Kpi label={t('Toplam Soru')} value={fmtN(counts.all)} />
          <Kpi label={t('Cevaplanma Oranı')} value={answerRate === null ? '—' : `%${answerRate}`}
            color={answerRate === null ? undefined : answerRate >= 90 ? T.greenText : answerRate >= 70 ? T.amberText : T.redText} />
          <Kpi label={t('Ort. İlk Cevap Süresi')} value={fmtDur(median(replyTimes), t)} />
          <Kpi label={t('Kaçırılan Soru')} value={fmtN(counts.missed)} color={counts.missed ? T.redText : undefined} />
        </div>
      </div>

      {/* Kanal performansı */}
      {perMarket.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : `repeat(${Math.min(perMarket.length, 3)}, minmax(0, 1fr))`, gap: 10 }}>
          {perMarket.map(p => (
            <button key={p.m} onClick={() => setMarket(market === p.m ? '' : p.m)} style={{ textAlign: 'left' as const, fontFamily: 'inherit', cursor: 'pointer',
              background: T.surface, border: `1px solid ${market === p.m ? marketColor(p.m) : T.border}`, borderRadius: 14, padding: '14px 16px',
              boxShadow: market === p.m ? `0 0 0 3px ${tint(marketColor(p.m), 80)}` : 'none' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <MarketChip name={p.m} />
                <span style={{ fontSize: 11, color: T.textDim }}>{fmtN(p.total)} {t('soru')}</span>
              </div>
              <div style={{ display: 'flex', height: 8, borderRadius: 999, overflow: 'hidden', background: 'var(--py-track-bg)', marginBottom: 10 }}>
                {[[p.ans, T.green], [p.pen, T.accent], [p.rej, T.amber], [p.mis, T.red]].map(([n, c], i) =>
                  (n as number) > 0 ? <span key={i} style={{ width: `${(n as number) / p.total * 100}%`, background: c as string }} /> : null)}
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' as const, fontSize: 11, color: T.textMid }}>
                <span><b style={{ color: T.greenText }}>{p.ans}</b> {t('Cevaplanan').toLocaleLowerCase('tr')}</span>
                {p.pen > 0 && <span><b style={{ color: T.amberDeepText }}>{p.pen}</b> {t('Bekleyen').toLocaleLowerCase('tr')}</span>}
                <span><b style={{ color: p.mis ? T.redText : T.textDim }}>{p.mis}</b> {t('Kaçırılan').toLocaleLowerCase('tr')}</span>
                {p.rej > 0 && <span><b style={{ color: T.amberText }}>{p.rej}</b> {t('Reddedilen').toLocaleLowerCase('tr')}</span>}
                <span style={{ marginLeft: 'auto', color: T.textDim }}>{t('cevap oranı')} <b style={{ color: T.text }}>{p.rate === null ? '—' : `%${p.rate}`}</b> · {t('ort. cevap')} <b style={{ color: T.text }}>{fmtDur(p.med, t)}</b></span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Kaçırılan sorular uyarısı */}
      {counts.missed > 0 && activeFilter !== 'missed' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' as const, padding: '12px 16px', borderRadius: 14,
          background: T.redTint, border: `1px solid ${tint(T.red, 45)}` }}>
          <span style={{ color: T.redText, display: 'flex' }}><Icon.warningTriangle size={16} sw={2} /></span>
          <div style={{ flex: 1, minWidth: 220, fontSize: 12.5, color: T.text, lineHeight: 1.5 }}>
            <b>{fmtN(counts.missed)}</b> {t('soru süresinde cevaplanmadığı için pazaryeri tarafından kapatıldı.')}{' '}
            <span style={{ color: T.textMid }}>{t('Bu sorular, müşterinin satın alma kararı verdiği anda cevapsız kaldı.')}</span>
          </div>
          <Button variant="soft" color={T.red} onClick={() => setFilter('missed')} style={{ padding: '6px 12px', fontSize: 11.5 }}>{t('Kaçırılanları Gör')}</Button>
        </div>
      )}

      {/* Gelen kutusu */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden',
        display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'minmax(320px, 400px) minmax(0, 1fr)', height: isMobile ? 'auto' : 640 }}>
        {isMobile ? (selected ? threadPane : <div style={{ maxHeight: 620, display: 'flex', flexDirection: 'column' }}>{listPane}</div>) : <>{listPane}{threadPane}</>}
      </div>
    </div>
  );
}

function Thread({ clientId, q, onBack, onSent }: { clientId: number; q: any; onBack?: () => void; onSent: () => void }) {
  const { t } = useLang();
  const [sug, setSug] = useState<any>(null);
  const [loadingSug, setLoadingSug] = useState(false);
  const [text, setText] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const endRef = React.useRef<HTMLDivElement>(null);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [q.id]);

  const loadSug = async () => {
    setLoadingSug(true);
    try {
      const r = await axios.get(`${API}/pixa/${clientId}/questions/${q.id}/suggestions`);
      setSug(r.data);
      if (r.data?.ai) setText(r.data.ai);
    } catch (e: any) { setSug({ ai: null, canned: [], errors: [e?.response?.data?.detail || t('Hata')] }); }
    finally { setLoadingSug(false); }
  };

  const send = async () => {
    setBusy(true); setErr(null);
    try {
      await axios.post(`${API}/pixa/${clientId}/questions/${q.id}/answer`, { answer: text.trim(), confirm: 'ONAYLA' });
      setConfirmOpen(false); setSent(true); onSent();
    } catch (e: any) { setErr(e?.response?.data?.detail || t('Hata')); }
    finally { setBusy(false); }
  };

  const who = (from: string) => from === 'Customer' ? t('Müşteri') : from === 'Merchant' ? t('Mağaza') : t('Sistem');
  const closedNote = q.status === 'missed' ? t('Bu soru süresinde cevaplanmadığı için pazaryeri tarafından kapatıldı; artık cevap gönderilemez.')
    : q.status === 'rejected' ? t('Pazaryeri bu soruyu reddetti; cevap gönderilemez.')
    : q.status === 'pending' && q.expired ? t('Bu sorunun cevaplama süresi doldu.')
    : null;
  const p = q.product;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
      {/* Başlık */}
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' as const }}>
        {onBack && <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textMid, fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, padding: '4px 6px 4px 0' }}>← {t('Geri')}</button>}
        <MarketChip name={q.market} />
        <StatusPill status={sent ? 'approval' : q.status} raw={q.status === 'other' ? q.status : undefined} />
        {q.topic && <span style={{ fontSize: 11, color: T.textDim, background: 'var(--py-subtle-bg)', borderRadius: 999, padding: '2px 9px' }}>{q.topic}</span>}
        <span style={{ marginLeft: 'auto', fontSize: 11, color: T.textFaint, display: 'flex', gap: 10, flexWrap: 'wrap' as const }}>
          {q.platformIssueNumber && <span>{t('Soru No')} <span style={{ fontFamily: MONO, color: T.textDim }}>{q.platformIssueNumber}</span></span>}
          {q.orderNumber && <span>{t('Sipariş')} <span style={{ fontFamily: MONO, color: T.textDim }}>#{q.orderNumber}</span></span>}
          <span>{fmtDateTime(q.askedAt)}</span>
        </span>
      </div>

      <div style={{ flex: 1, overflowY: 'auto' as const, minHeight: 0, padding: 16, display: 'flex', flexDirection: 'column', gap: 12, background: 'var(--py-subtle-bg)' }}>
        {/* Ürün kartı */}
        {p ? (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 10 }}>
            <Thumb url={q.thumb} size={52} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{p.name}</div>
              <div style={{ fontSize: 11, color: T.textDim, marginTop: 3, display: 'flex', gap: 10, flexWrap: 'wrap' as const }}>
                {p.sku && <span>{t('SKU')} <span style={{ fontFamily: MONO }}>{p.sku}</span></span>}
                {p.salePrice && <span>{money(p.salePrice)}</span>}
                {p.stockQuantity !== undefined && p.stockQuantity !== null && <span>{t('Stok')} {fmtN(p.stockQuantity)}</span>}
              </div>
            </div>
          </div>
        ) : q.productName ? (
          <div style={{ fontSize: 12, color: T.textMid, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: '10px 12px' }}>{q.productName}</div>
        ) : null}

        {/* Konuşma */}
        {q.convs.length === 0 && <Bubble side="left" label={t('Müşteri')} text={q.text || '—'} time={q.askedAt} />}
        {q.convs.map((c: any, i: number) => {
          if (c.from === 'System') return (
            <div key={c.id || i} style={{ alignSelf: 'center', maxWidth: '85%', textAlign: 'center' as const, fontSize: 11.5, color: T.textMid, lineHeight: 1.5,
              background: T.surface, border: `1px dashed ${T.borderMid}`, borderRadius: 10, padding: '8px 12px' }}>
              <b style={{ color: T.textDim }}>{q.market}</b> · {c.content}
              <div style={{ fontSize: 10, color: T.textFaint, marginTop: 3 }}>{fmtDateTime(c.platformCreationDate)}</div>
            </div>
          );
          const prev = q.convs.slice(0, i).reverse().find((x: any) => x.from === 'Customer');
          const gap = c.from === 'Merchant' && prev ? tsOf(c.platformCreationDate) - tsOf(prev.platformCreationDate) : null;
          return <Bubble key={c.id || i} side={c.from === 'Customer' ? 'left' : 'right'} label={who(c.from)} text={c.content}
            time={c.platformCreationDate} footer={gap !== null && gap >= 0 ? `${fmtDur(gap, t)} ${t('sonra cevaplandı')}` : undefined} />;
        })}
        {sent && <Bubble side="right" label={t('Mağaza')} text={text} time={new Date().toISOString()} footer={t('Onay Bekliyor')} />}
        <div ref={endRef} />
      </div>

      {/* Cevap alanı */}
      <div style={{ borderTop: `1px solid ${T.border}`, padding: 12, background: T.surface }}>
        {sent ? (
          <div style={{ fontSize: 12, color: T.greenText, fontWeight: 700 }}>✓ {t('Gönderildi')}</div>
        ) : q.answerable ? (
          <>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const, alignItems: 'center', marginBottom: 8 }}>
              <Button variant="soft" color={T.accent} onClick={loadSug} disabled={loadingSug} style={{ padding: '6px 12px', fontSize: 11.5 }}>
                <Icon.sparkle size={12} sw={2} /> {loadingSug ? t('AI taslağı hazırlanıyor…') : sug ? t('Yeniden Yaz') : t('AI ile Cevap Yaz')}
              </Button>
              {(sug?.canned || []).slice(0, 4).map((c: any, i: number) => {
                const ct = typeof c === 'string' ? c : (c.answer || c.text || c.content || '');
                return ct ? (
                  <button key={i} onClick={() => setText(ct)} title={ct} style={{ fontFamily: 'inherit', fontSize: 11, color: T.textMid, background: 'var(--py-subtle-bg)',
                    border: `1px solid ${T.border}`, borderRadius: 999, padding: '4px 10px', cursor: 'pointer', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
                    {ct.slice(0, 40)}
                  </button>
                ) : null;
              })}
            </div>
            {(sug?.errors || []).map((e: string, i: number) => <div key={i} style={{ fontSize: 11.5, color: T.redText, marginBottom: 6 }}>{e}</div>)}
            <textarea value={text} onChange={e => setText(e.target.value)} rows={3} placeholder={t('Pazaryerinde yayınlanacak cevabı yazın…')}
              style={{ ...inputStyle, width: '100%', resize: 'vertical' as const, lineHeight: 1.6, fontSize: 13, boxSizing: 'border-box' as const }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' as const }}>
              <div style={{ fontSize: 10.5, color: T.textDim }}>
                {q.expireDate && <>{t('Son cevaplama')}: <b>{fmtDateTime(q.expireDate)}</b> · </>}
                {t('Cevap pazaryerinde herkese açık yayınlanır ve geri alınamaz.')}
              </div>
              <Button variant="solid" color={T.accent} disabled={!text.trim()} onClick={() => { setErr(null); setConfirmOpen(true); }}
                style={{ padding: '7px 16px', fontSize: 12, color: '#fff', opacity: text.trim() ? 1 : 0.5 }}>
                <Icon.send size={12} sw={2.2} /> {t('Pazaryerine Gönder')}
              </Button>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: T.textMid }}>
            <span style={{ width: 8, height: 8, borderRadius: 999, background: STATUS_META[q.status as QStatus].color, flexShrink: 0 }} />
            {closedNote || t('Bu soru cevaplandı.')}
            {q.status === 'answered' && q.replyMs !== null && <span style={{ marginLeft: 'auto', color: T.textDim }}>{t('Cevap süresi')}: <b style={{ color: T.text }}>{fmtDur(q.replyMs, t)}</b></span>}
          </div>
        )}
      </div>
      <ConfirmDangerModal open={confirmOpen} busy={busy} error={err}
        title={t('Cevabı pazaryerinde yayınla')}
        description={t('Bu cevap pazaryerinde herkese açık olarak yayınlanacak ve geri alınamaz.')}
        items={[text.slice(0, 200)]}
        confirmLabel={t('Gönder')} onCancel={() => setConfirmOpen(false)} onConfirm={send} />
    </div>
  );
}

function Bubble({ side, label, text, time, footer }: { side: 'left' | 'right'; label: string; text: string; time?: string; footer?: string }) {
  const right = side === 'right';
  return (
    <div style={{ alignSelf: right ? 'flex-end' : 'flex-start', maxWidth: '82%', display: 'flex', flexDirection: 'column', alignItems: right ? 'flex-end' : 'flex-start' }}>
      <div style={{ fontSize: 10.5, color: T.textFaint, marginBottom: 4, padding: '0 4px' }}>
        <b style={{ color: T.textDim }}>{label}</b>{time ? ` · ${fmtDateTime(time)}` : ''}
      </div>
      <div style={{ whiteSpace: 'pre-wrap' as const, fontSize: 13, lineHeight: 1.6, padding: '10px 14px',
        borderRadius: right ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
        background: right ? tint(T.green, 12) : T.surface, color: T.text,
        border: `1px solid ${right ? tint(T.green, 40) : T.border}` }}>{text}</div>
      {footer && <div style={{ fontSize: 10.5, color: T.greenText, fontWeight: 700, marginTop: 4, padding: '0 4px' }}>{footer}</div>}
    </div>
  );
}

// ── Kurallar ─────────────────────────────────────────────────────────────────
function Rules({ clientId }: { clientId: number }) {
  const { t } = useLang();
  const [rules, setRules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState<{ msg: string; tone: 'ok' | 'err' }>({ msg: '', tone: 'ok' });

  const load = () => {
    setLoading(true);
    axios.get(`${API}/pixa/${clientId}/anomalies/rules`)
      .then(r => setRules(r.data?.rules || [])).catch(() => setRules([])).finally(() => setLoading(false));
  };
  useEffect(load, [clientId]);

  const patch = async (kind: string, body: any) => {
    try {
      await axios.patch(`${API}/pixa/${clientId}/anomalies/rules`, { kind, ...body });
      setNote({ msg: t('Kaydedildi.'), tone: 'ok' }); load();
    } catch (e: any) { setNote({ msg: e?.response?.data?.detail || t('Hata'), tone: 'err' }); }
    finally { setTimeout(() => setNote({ msg: '', tone: 'ok' }), 3000); }
  };

  if (loading) return <Loader />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 12, color: T.textDim, lineHeight: 1.6 }}>
        {t('Hangi durumların uyarı üreteceğini ve önem derecelerini buradan ayarlayın. Tarama saatte bir otomatik çalışır.')}
        <Toast msg={note.msg} tone={note.tone} />
      </div>
      {rules.map((r: any) => (
        <Card key={r.kind} pad={16}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' as const }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: r.enabled ? T.text : T.textDim }}>{titleCase(r.title)}</span>
                {r.mute_until && <Badge color={T.textDim}>{t('Susturuldu')}</Badge>}
              </div>
              {r.params?.note && (
                <div style={{ fontSize: 11, color: T.amberText, background: T.amberTint, padding: '7px 9px', borderRadius: 7, marginTop: 8, lineHeight: 1.55 }}>
                  {r.params.note}
                </div>
              )}
              {(r.params?.thresholdPct !== undefined || r.params?.minMarginPct !== undefined) && (
                <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap' as const }}>
                  {r.params.thresholdPct !== undefined && (
                    <label style={{ fontSize: 11, color: T.textMid, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {t('Sapma eşiği %')}
                      <input type="number" min={0} max={100} defaultValue={r.params.thresholdPct}
                        onBlur={e => patch(r.kind, { params: { thresholdPct: Number(e.target.value) } })}
                        style={{ ...inputStyle, width: 70, fontFamily: MONO }} />
                    </label>
                  )}
                  {r.params.minMarginPct !== undefined && (
                    <label style={{ fontSize: 11, color: T.textMid, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {t('Asgari marj %')}
                      <input type="number" min={0} max={100} defaultValue={r.params.minMarginPct}
                        onBlur={e => patch(r.kind, { params: { minMarginPct: Number(e.target.value) } })}
                        style={{ ...inputStyle, width: 70, fontFamily: MONO }} />
                    </label>
                  )}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' as const }}>
              <select value={r.severity} onChange={e => patch(r.kind, { severity: e.target.value })} style={{ ...inputStyle, minWidth: 100 }}>
                <option value="high">{t('Kritik')}</option>
                <option value="medium">{t('Orta')}</option>
                <option value="low">{t('Düşük')}</option>
                <option value="info">{t('Bilgi')}</option>
              </select>
              {r.mute_until
                ? <Button variant="ghost" color={T.accent} onClick={() => patch(r.kind, { mute_hours: 0 })} style={{ padding: '5px 10px', fontSize: 11 }}>{t('Susturma')}</Button>
                : <Button variant="ghost" color={T.textDim} onClick={() => patch(r.kind, { mute_hours: MUTE_FOREVER_HOURS })} style={{ padding: '5px 10px', fontSize: 11 }}>{t('Sustur')}</Button>}
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: T.textMid, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!r.enabled} onChange={e => patch(r.kind, { enabled: e.target.checked })} style={{ cursor: 'pointer' }} />
                {t('Açık')}
              </label>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
